import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ActionIcon, Button, Flex, Group, Kbd, Paper, Switch, Text, Textarea, TextInput, Tooltip } from "@mantine/core";
import { Prism } from "@mantine/prism";
import { IconEdit } from "@tabler/icons-react";
import { Tenant } from "src/graphql/generated/graphql";
import { handleError } from "src/utils/error";

import { useUpdateTenantSEO } from "../../../../hooks/tenants";
import { FormItem } from "../../../FormItem";
import { Link } from "../../../Link";

export interface SEOAnalyticsProps {
    tenant: Tenant;
    onTenantUpdate?();
}

export const SEOAnalytics = ({ tenant, onTenantUpdate }: SEOAnalyticsProps) => {
    const { t } = useTranslation(["common"]);

    const [enableUpdate, setEnableUpdate] = useState(false);
    const [gtmEnable, setGtmEnable] = useState(!!tenant?.config?.seo?.gtmId);
    const [gtmId, setGtmId] = useState(tenant?.config?.seo?.gtmId);
    const [gtmLoadAfterInteractive, setGtmLoadAfterInteractive] = useState(
        !!tenant?.config?.seo?.gtmLoadAfterInteractive
    );
    const [customScriptHead, setCustomScriptHead] = useState(tenant?.config?.seo?.customScriptHead);

    useEffect(() => {
        setGtmEnable(!!tenant?.config?.seo?.gtmId);
        setGtmId(tenant?.config?.seo?.gtmId);
        setGtmLoadAfterInteractive(!!tenant?.config?.seo?.gtmLoadAfterInteractive);
        setCustomScriptHead(tenant?.config?.seo?.customScriptHead);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(tenant?.config?.seo)]);

    const { updateTenantSEO } = useUpdateTenantSEO(tenant?.id);

    const handleSubmit = useCallback(async () => {
        try {
            await updateTenantSEO({ gtmId, gtmLoadAfterInteractive, customScriptHead });

            onTenantUpdate?.();
        } catch (err) {
            handleError(err, { context: { tenantId: tenant?.id } });
        } finally {
            setEnableUpdate(false);
        }
    }, [updateTenantSEO, gtmId, gtmLoadAfterInteractive, customScriptHead, onTenantUpdate, tenant?.id]);

    return (
        <Paper p={36} radius={12}>
            <Flex justify="space-between">
                <Text mb="md" size="lg" weight={500}>
                    {t("tenant_seo_gtm")}
                </Text>
                {!enableUpdate && (
                    <Tooltip label={t("tenant_seo_update")}>
                        <ActionIcon variant="subtle" color="orange" onClick={() => setEnableUpdate(true)}>
                            <IconEdit size="1em" />
                        </ActionIcon>
                    </Tooltip>
                )}
            </Flex>
            <Flex mb="md" gap="xl" wrap="wrap">
                <Switch
                    mb="md"
                    mr={enableUpdate ? undefined : "md"}
                    color={enableUpdate ? "orange" : "gray"}
                    label={<Text weight={500}>{t("tenant_seo_gtm_enable")}</Text>}
                    style={{ width: enableUpdate ? "100%" : undefined }}
                    checked={gtmEnable}
                    onChange={(event) => {
                        if (!enableUpdate) {
                            return;
                        }

                        if (event?.target?.checked) {
                            setGtmEnable(true);
                            return;
                        }

                        setGtmEnable(false);
                        setGtmId(null);
                    }}
                />
                <FormItem
                    mb="md"
                    mr={enableUpdate ? undefined : "md"}
                    label={t("tenant_seo_gtmid")}
                    style={{ width: enableUpdate ? "100%" : undefined }}
                >
                    {enableUpdate ? (
                        <TextInput
                            placeholder="GTM-XXXXXX"
                            description={
                                <>
                                    {t("tenant_seo_gtmid_description")}:{" "}
                                    <Text
                                        component={Link}
                                        size="xs"
                                        color="orange"
                                        href="https://support.google.com/tagmanager/answer/6103696"
                                        target="_blank"
                                    >
                                        https://support.google.com/tagmanager/answer/6103696
                                    </Text>
                                </>
                            }
                            disabled={!gtmEnable}
                            value={gtmId || ""}
                            onChange={(event) => setGtmId(event?.target?.value)}
                        />
                    ) : (
                        <Kbd>{gtmId || "-"}</Kbd>
                    )}
                </FormItem>
            </Flex>
            <Switch
                color={enableUpdate ? "orange" : "gray"}
                label={<Text weight={500}>{t("tenant_seo_gtmloadafterinteractive")}</Text>}
                style={{ width: enableUpdate ? "100%" : undefined }}
                checked={gtmLoadAfterInteractive}
                onChange={(event) => {
                    if (!enableUpdate) {
                        return;
                    }
                    setGtmLoadAfterInteractive(!!event?.target?.checked);
                }}
            />
            <Text mt="xl" mb="md" size="lg" weight={500}>
                {t("tenant_seo_customcode")}
            </Text>
            <FormItem label={t("tenant_seo_customheadscript")}>
                {enableUpdate ? (
                    <Textarea
                        description={t("tenant_seo_customheadscript_description")}
                        placeholder={t("tenant_seo_customheadscript")}
                        value={customScriptHead}
                        onChange={(event) => setCustomScriptHead(event?.target?.value)}
                    />
                ) : customScriptHead ? (
                    <Prism mt="xs" language="javascript" withLineNumbers>
                        {customScriptHead}
                    </Prism>
                ) : (
                    "-"
                )}
            </FormItem>
            {enableUpdate && (
                <Group mt="md" spacing="sm" position="right">
                    <Button
                        variant="subtle"
                        onClick={() => {
                            setEnableUpdate(false);
                            setGtmId(tenant?.config?.seo?.gtmId);
                            setCustomScriptHead(tenant?.config?.seo?.customScriptHead);
                        }}
                    >
                        {t("cancel")}
                    </Button>
                    <Button onClick={handleSubmit}>{t("save")}</Button>
                </Group>
            )}
        </Paper>
    );
};
